import { ACCESS_PENDING_RETURN_PATH } from '../../../lib/sessionExpiry';
import { isLearnSurfacePath, parseSafeReturn, resolvePostAuthDestination } from '../../../lib/safeReturn';

export const FAMILY_HOME_PATH = '/family';
export const CHOOSE_ROLE_PATH = '/choose-role';
export const CURRENT_FAMILY_PARENT_ONBOARDING_VERSION = 1;

/** Session-scoped escape hatch from a /choose-role screen that cannot save.
 * Set only after the chooser's own probe reports the surface `unavailable`
 * and the user takes the explicit "Continue to practice" way out. It lives in
 * `sessionStorage` on purpose: the account type is still genuinely unknown, so
 * the gate must come back on a new session rather than be suppressed forever.
 */
export const CHOOSE_ROLE_SUPPRESS_KEY = 'pp.family.chooseRoleUnavailable';

function suppressionStorageKey(user?: FamilyAccountUser | null): string | null {
  const email = typeof user?.email === 'string' ? user.email.trim().toLowerCase() : null;
  return email ? `${CHOOSE_ROLE_SUPPRESS_KEY}:${email}` : null;
}

export function isChooseRoleSuppressed(user?: FamilyAccountUser | null): boolean {
  const key = suppressionStorageKey(user);
  if (!key) return false;
  try {
    return window.sessionStorage.getItem(key) === '1';
  } catch {
    // A storage-denied browser simply keeps the gate on. Never guess.
    return false;
  }
}

export function suppressChooseRole(user?: FamilyAccountUser | null): void {
  const key = suppressionStorageKey(user);
  if (!key) return;
  try {
    window.sessionStorage.setItem(key, '1');
  } catch {
    /* storage denied — the gate stays on, which is the safe direction */
  }
}

/** End the temporary chooser escape at an explicit auth boundary so a new
 * identity in the same tab cannot inherit another account's decision. */
export function clearChooseRoleSuppression(): void {
  try {
    window.sessionStorage.removeItem(CHOOSE_ROLE_SUPPRESS_KEY);
    const keysToRemove: string[] = [];
    for (let index = 0; index < window.sessionStorage.length; index += 1) {
      const key = window.sessionStorage.key(index);
      if (key?.startsWith(`${CHOOSE_ROLE_SUPPRESS_KEY}:`)) keysToRemove.push(key);
    }
    for (const key of keysToRemove) window.sessionStorage.removeItem(key);
  } catch {
    /* storage denied — there is no readable suppression state to clear */
  }
}

type FamilyAccountUser = {
  email?: unknown;
  family_account_type?: unknown;
  family_parent_onboarding_version?: unknown;
  [key: string]: unknown;
};

export function readParentOnboardingVersion(
  user: FamilyAccountUser | null | undefined,
): number {
  const raw = user?.family_parent_onboarding_version;
  if (raw === null || raw === undefined) return 0;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
}

function normalizeRoutePath(pathname: string): string {
  const path = (pathname || '').split(/[?#]/, 1)[0].replace(/\/+$/, '');
  return (path || '/').toLowerCase();
}

export function isChooseRolePath(pathname: string): boolean {
  return normalizeRoutePath(pathname) === CHOOSE_ROLE_PATH;
}

export function isFamilyPath(pathname: string): boolean {
  const path = normalizeRoutePath(pathname);
  return path === '/family' || path.startsWith('/family/');
}

/** The Product Learn surface is gated server-side, not by account type (O-C1b). */
export function isLearnPath(pathname: string): boolean {
  return isLearnSurfacePath(normalizeRoutePath(pathname));
}

export function isGuardianAccount(
  user: FamilyAccountUser | null | undefined,
): boolean {
  return user?.family_account_type === 'guardian';
}

export function isUntypedAccount(
  user: FamilyAccountUser | null | undefined,
): boolean {
  return !!user && user.family_account_type == null;
}

export function needsStudentParentOnboarding(
  user: FamilyAccountUser | null | undefined,
): boolean {
  return (
    user?.family_account_type === 'student'
    && readParentOnboardingVersion(user) < CURRENT_FAMILY_PARENT_ONBOARDING_VERSION
  );
}

export function shouldRedirectGuardianToFamily(
  user: FamilyAccountUser | null | undefined,
  pathname: string,
): boolean {
  return isGuardianAccount(user) && !isFamilyPath(pathname) && !isLearnPath(pathname);
}

export function shouldRedirectNonGuardianFromFamily(
  user: FamilyAccountUser | null | undefined,
  pathname: string,
): boolean {
  return !!user && isFamilyPath(pathname) && !isGuardianAccount(user);
}

/** Send verified users to the production first-run chooser when required. */
export function shouldRedirectToChooseRole(
  user: FamilyAccountUser | null | undefined,
  pathname: string,
): boolean {
  if (!user || isChooseRoleSuppressed(user)) return false;
  if (isChooseRolePath(pathname) || isFamilyPath(pathname) || isLearnPath(pathname)) return false;
  if (isUntypedAccount(user)) return true;
  return needsStudentParentOnboarding(user);
}

/** A user who cannot use the chooser anymore should leave /choose-role. */
export function shouldRedirectTypedUserFromChooseRole(
  user: FamilyAccountUser | null | undefined,
  pathname: string,
): boolean {
  if (!isChooseRolePath(pathname)) return false;
  const accountType = user?.family_account_type;
  if (accountType === 'guardian') return true;
  if (accountType === 'student') {
    return readParentOnboardingVersion(user) >= CURRENT_FAMILY_PARENT_ONBOARDING_VERSION;
  }
  return false;
}

/** Account type selects Product home. It never authorizes child data. */
export function resolveSignedInDestination(
  user: FamilyAccountUser | null | undefined,
  candidate?: unknown,
): string {
  if (candidate === ACCESS_PENDING_RETURN_PATH) return ACCESS_PENDING_RETURN_PATH;
  if (!isGuardianAccount(user)) {
    const safe = parseSafeReturn(candidate);
    const destination = safe && isFamilyPath(safe)
      ? resolvePostAuthDestination(undefined)
      : resolvePostAuthDestination(candidate);
    if (destination === CHOOSE_ROLE_PATH) {
      return shouldRedirectToChooseRole(user, '/practice')
        ? CHOOSE_ROLE_PATH
        : resolvePostAuthDestination(undefined);
    }
    return destination;
  }
  const safe = parseSafeReturn(candidate);
  return safe && (isFamilyPath(safe) || isLearnPath(safe)) ? safe : FAMILY_HOME_PATH;
}
